/* eslint-disable camelcase */
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import React, { useContext, useState, useCallback } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import getCurrencySymbol from 'currency-symbol-map';
import PropTypes from 'prop-types';

import { FormPaymentFieldIndicator, Img, UiIndicator, PayPalButtonWrapper } from '../../common-field-styles';
import { confirmPayment, setUserInputOnCtrl } from '../../../form/reducer/form';
import { onPaymentValueChange } from '../../../form/slice-actions/form';
import { FormContext } from '../../../shared/contexts/form-context';
import { getLabel } from '../../../../shared/form-lib';
import { ControlPropType } from '../../ctrl_proptype';
import { logToServer } from '../../../form/utils';

const imgSrc = 'https://storage.googleapis.com/fplsblog/PayPal_Gold_Original.png';

const PayPalSubComponent = ({ ctrl, formDesign, isRenderedOnModal }) => {
  const dispatch = useDispatch();
  const { inForm, currentPageNo } = useContext(FormContext);
  const {
    payments,
    userAmount,
    isPayOnSubmission,
    editableForm: { paypal_public_key, form_options, user_custom_texts },
  } = useSelector((state) => state.form);
  const { isPaid, isError, message } = payments[ctrl.type][ctrl.internal_id] || {};
  const finalAmount = Number(userAmount[ctrl.internal_id] ?? ctrl.props.amount);
  const invalidAmount = finalAmount <= 0;
  const modeStatusText = !form_options?.paypal_live_mode ? '(sandbox mode)' : '';
  const submitButtonText =
    inForm && (formDesign.old.submission_button_text || getLabel('submit_text', user_custom_texts) || 'Submit');
  const [paying, setPaying] = useState(false);

  const amountToPay = `${getCurrencySymbol(ctrl.props.currency || 'USD')}${finalAmount}`;

  const handleStorePaymentValue = (status, displayMsg, newValue, transactionId = '') => {
    batch(() => {
      if (status === 'paid') {
        dispatch(
          setUserInputOnCtrl({
            newValue,
            property: 'value',
            page: currentPageNo - 1,
            ctrlId: ctrl.internal_id,
            isValid: true,
          })
        );

        if (ctrl.value !== newValue) {
          dispatch(onPaymentValueChange());
        }
      }

      dispatch(
        confirmPayment({
          type: ctrl.type,
          isPaid: status === 'paid',
          isError: status === 'error',
          message: displayMsg,
          ctrlId: ctrl.internal_id,
          transactionId,
        })
      );
    });
  };

  // Create a paypal order. Set up the details of the transaction
  const createOrder = (data, actions) => {
    return actions.order
      .create({
        purchase_units: [
          {
            amount: {
              currency_code: ctrl.props.currency,
              value: finalAmount,
            },
          },
        ],
      })
      .then((orderID) => {
        return orderID;
      });
  };

  // Check Approval. Capture the fund from the transaction
  const onApprove = (data, actions) => {
    return actions.order.capture().then((details) => {
      const success = details.status === 'COMPLETED';
      setPaying(true);
      const amount = details.purchase_units[0].amount.value;
      const currency = details.purchase_units[0].amount.currency_code;

      if (success) {
        setPaying(false);
        const newValue = `Amount Paid: ${amount} ${currency}\nTransaction ID: ${details.id}\n`;
        const displayMsg = `Payment of ${amount} ${currency} approved. Kindly click on “${submitButtonText}” at the bottom of the form to complete transaction`;
        handleStorePaymentValue('paid', displayMsg, newValue, details.id);
      } else {
        setPaying(false);
      }

      const payload = {
        form_id: window.FORM_ID,
        transaction_id: details.id,
        amount,
        currency,
        status: success ? `success ${modeStatusText}` : `failure ${modeStatusText}`,
        reason: success ? 'Payment successful.' : 'Payment failed.',
        gateway: 'Paypal',
      };

      logToServer(payload);
    });
  };

  // Capture possible error
  const onError = (data, actions) => {
    const invalidAmountMsg = 'An error occurred. Please, add an amount before proceeding with payment';
    const payload = {
      form_id: window.FORM_ID,
      transaction_id: `formplus-${Math.random()}`,
      amount: finalAmount,
      currency: ctrl.props.currency,
      status: `failure ${modeStatusText}`,
      reason: `Payment failed. ${invalidAmount ? invalidAmountMsg : ''}`,
      gateway: 'Paypal',
    };
    const displayMsg = invalidAmount ? invalidAmountMsg : 'An error occured with your payment';
    handleStorePaymentValue('error', displayMsg);
    logToServer(payload);
  };

  const styleOptions = {
    layout: 'vertical',
    color: 'gold',
    shape: 'rect',
    label: 'paypal',
    height: 45,
  };

  const scriptOptions = {
    'client-id': paypal_public_key,
    components: 'buttons',
    currency: ctrl.props.currency,
  };

  const ButtonWrapper = useCallback(() => {
    const [{ isPending, isRejected, isResolved }] = usePayPalScriptReducer();
    return (
      <>
        {isPending && <p className="ui label">Please wait while Paypal button loads...</p>}
        {isRejected && <p className="ui label">Invalid Paypal key provided</p>}
        {isResolved && (
          <PayPalButtons
            createOrder={(data, actions) => createOrder(data, actions)}
            onApprove={(data, actions) => onApprove(data, actions)}
            onError={(data, actions) => onError(data, actions)}
            style={styleOptions}
          />
        )}
      </>
    );
  }, [finalAmount]);

  return (
    <>
      {isPayOnSubmission && !isRenderedOnModal ? (
        <FormPaymentFieldIndicator>
          There is a {ctrl.props.label} payment field attached to this form. You may be required to make payment of{' '}
          {amountToPay} after submission.
        </FormPaymentFieldIndicator>
      ) : (
        <PayPalButtonWrapper isRenderedOnModal={isRenderedOnModal}>
          {inForm && isRenderedOnModal && <div className="label">{`${ctrl.props.label} - ${amountToPay}`}</div>}

          {/* Dummy button shown when not in form view */}
          {!inForm && (
            <div>
              <Img src={imgSrc} alt="Paypal builder" height={140} />
            </div>
          )}
          {inForm && !paypal_public_key && (
            <UiIndicator className="status red">
              <span>
                The form owner has not set up their payment integration. Please contact the owner for more information.
              </span>
            </UiIndicator>
          )}
          {(isPaid || ctrl.value) && (
            <UiIndicator className="paid">
              <i className="check icon" />
              <span>{message || ctrl.value}</span>
            </UiIndicator>
          )}
          {/* Messsage shown when error is returned from calling the payment APIs */}
          {isError && (
            <UiIndicator className="status red">
              <span>{message || 'An error occured with your payment.'}</span>
            </UiIndicator>
          )}
          {inForm && !isPaid && !ctrl.value && (
            <>
              {!paypal_public_key ? (
                <div>
                  <Img src={imgSrc} alt="Paypal builder" height={140} />
                </div>
              ) : (
                <PayPalScriptProvider options={scriptOptions}>
                  <ButtonWrapper />
                </PayPalScriptProvider>
              )}
            </>
          )}
          {inForm && !form_options?.paypal_live_mode && <UiIndicator className="mode">Sandbox mode</UiIndicator>}
          {inForm && isRenderedOnModal && (
            <UiIndicator className={ctrl.props.required ? 'required' : 'optional'}>
              {ctrl.props.required ? '*Required' : 'Optional'}
            </UiIndicator>
          )}
          {paying && <div className="inline ui active tiny loader" />}
        </PayPalButtonWrapper>
      )}
    </>
  );
};

export default PayPalSubComponent;

PayPalSubComponent.defaultProps = {
  isRenderedOnModal: false,
  formDesign: null,
};

PayPalSubComponent.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  isRenderedOnModal: PropTypes.bool,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
